/**
 * Session Timeout Provider
 *
 * Context provider for managing session timeout globally across the app
 *
 * Features:
 * - Tracks session timeout based on user settings
 * - Shows warning modal 2 minutes before expiration
 * - Automatically signs out on expiration
 * - Allows session extension
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { SessionTimeoutWarningModal } from '../pages/settings/components/SessionTimeoutWarningModal';
import { supabaseAuthService } from '../services/supabase-service';

interface SessionTimeoutContextType {
  extendSession: () => Promise<boolean>;
  resetTimer: () => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

export function useSessionTimeoutContext() {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeoutContext must be used within SessionTimeoutProvider');
  }
  return context;
}

interface SessionTimeoutProviderProps {
  children: React.ReactNode;
}

export function SessionTimeoutProvider({ children }: SessionTimeoutProviderProps): React.ReactElement {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const handleWarning = useCallback((timeRemainingMinutes: number) => {
    setTimeRemaining(timeRemainingMinutes);
    setShowWarning(true);
  }, []);

  // Sign-out is performed inside useSessionTimeout before this runs
  const handleExpire = useCallback(() => {
    setShowWarning(false);
    navigate('/login?sessionExpired=true', { replace: true });
  }, [navigate]);

  const { extendSession, resetTimer } = useSessionTimeout({
    onWarning: handleWarning,
    onExpire: handleExpire,
    enabled: true,
  });

  const handleExtendSession = useCallback(async (): Promise<boolean> => {
    const success = await extendSession();
    if (success) {
      setShowWarning(false);
    }
    return !!success;
  }, [extendSession]);

  const handleSignOut = useCallback(async () => {
    setShowWarning(false);
    try {
      await supabaseAuthService.signOut();
    } catch (error) {
      console.error('Error signing out from session timeout warning:', error);
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <SessionTimeoutContext.Provider value={{ extendSession: handleExtendSession, resetTimer }}>
      {children}
      <SessionTimeoutWarningModal
        isOpen={showWarning}
        timeRemainingMinutes={timeRemaining}
        onExtendSession={handleExtendSession}
        onSignOut={handleSignOut}
      />
    </SessionTimeoutContext.Provider>
  );
}
